import { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';

/**
 * Returns a stable, debounced version of `callback`.
 *
 * The latest `callback` is always the one invoked, so it may freely close over
 * render-scoped values. Pending calls are cancelled on unmount.
 *
 * @param callback Function to debounce.
 * @param wait Delay in milliseconds after the last call before invoking.
 * @returns The debounced function.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(callback: (...args: TArgs) => void, wait: number) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  const debounced = useMemo(() => _.debounce((...args: TArgs) => callbackRef.current(...args), wait), [wait]);

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}
